// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ChatMessage.test.tsx

/**
 * One message's Knowledge Database citations, in the two shapes it draws them.
 *
 * Beside the other citation helpers rather than inside `ChatMessage.tsx`: that
 * component was already well past the 400-line expert-legibility bar, and this
 * derivation is self-contained -- it reads a message's stored citations and
 * answers two questions about them, touching nothing else the component does.
 *
 * The two shapes are NOT interchangeable, which is the whole reason this is one
 * hook and not two calls at the call site:
 *
 * - `ruleCitations` is the deduped rule list. It is what the chips under the
 *   message are drawn and NUMBERED over.
 * - `claimLinks` is that same list as inline links in the prose, carrying the
 *   same numbers so a reader can tell which underlined phrase is which chip.
 *
 * Numbering and identity therefore come from the deduped list, while PROVENANCE
 * has to come from the RAW citations: `dedupeRuleCitations` drops every graph
 * record by design, and those records are what carry the chat or memory a claim
 * was discussed in. Deriving both here keeps that pairing in one place; reading
 * provenance off the deduped list was the first shape of this and every inline
 * link silently fell back to the registry.
 */

import { useMemo } from "react";

import {
  dedupeRuleCitations,
  type TicketMessageCitation,
} from "./commentSourceUtils";

import { claimCitationsFor } from "../../../components/ask-puntt-review/claimCitations";
import { type LinkableClaim } from "../../../plugins/rehype-claim-citation";

export interface MessageClaimCitations {
  /**
   * The rule citations, deduped on the visible quote. Filtered to the rule
   * variant so only the fetch-free, Rules-tab-deep-linking branch of
   * `CommentCitationLinks` renders -- the file/record branches need pane wiring
   * and a follow-up fetch the chat message list does not have. Deduped because
   * different rules backed by the same evidence line would otherwise render as
   * duplicate chips.
   */
  ruleCitations: ReturnType<typeof dedupeRuleCitations>;
  /**
   * The claims this answer QUOTES, as links in its own prose. The chips stay:
   * they focus the Cited Claims rail, which an inline link cannot do.
   */
  claimLinks: LinkableClaim[];
}

export function useMessageClaimCitations(
  citations: readonly TicketMessageCitation[] | undefined,
): MessageClaimCitations {
  const ruleCitations = useMemo(
    () => dedupeRuleCitations(citations ?? []),
    [citations],
  );

  const claimLinks = useMemo(
    () => claimCitationsFor({ citations: ruleCitations }, citations ?? []),
    [ruleCitations, citations],
  );

  return { ruleCitations, claimLinks };
}
